import React, { useState, useEffect } from 'react'
import {
  AcademicCapIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  StarIcon,
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { coursesApi, categoriesApi } from '../../services/adminApi'

// 接口定义
interface Course {
  id: string
  title: string
  description: string
  duration_minutes: number
  max_students: number
  price_per_session: number
  tags?: string[]
  language: string
  level: 'beginner' | 'intermediate' | 'advanced'
  category?: string
  thumbnail_url?: string
  requirements?: string[]
  objectives?: string[]
  status: 'draft' | 'active' | 'inactive' | 'suspended'
  teacher_id: number
  teacher_name: string
  teacher_email: string
  teacher_avatar?: string
  teacher_bio?: string
  teacher_rating?: number
  teacher_rate?: number
  average_rating: string
  review_count: number
  completed_sessions: number
  unique_students: number
  completion_rate: number
  created_at: string
  updated_at: string
}

interface CourseStats {
  overview: {
    total: number
    active: number
    draft: number
    suspended: number
  }
  by_category: Array<{ category: string; count: number }>
  by_language: Array<{ language: string; count: number }>
  by_level: Array<{ level: string; count: number }>
  recent_trend: Array<{
    date: string
    count: number
    active: number
    draft: number
  }>
  price_stats: {
    min: number
    max: number
    avg: string
  }
  popular_courses: Array<{
    id: string
    title: string
    booking_count: number
    avg_rating: string
  }>
  booking_stats: {
    total: number
    completed: number
    cancelled: number
    avg_duration: string
  }
}

interface Category {
  id?: string
  name: string
  name_en?: string
  description?: string
  description_en?: string
  created_at?: string
}

export const CourseManagement: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [courses, setCourses] = useState<Course[]>([])
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [stats, setStats] = useState<CourseStats | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  
  // 筛选和搜索状态
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [levelFilter, setLevelFilter] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 20

  // 表单状态
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    duration_minutes: 60,
    max_students: 10,
    price_per_session: 100,
    tags: [] as string[],
    language: '',
    level: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    category: '',
    thumbnail_url: '',
    requirements: [] as string[],
    objectives: [] as string[],
    teacher_id: 0,
    status: 'draft' as 'draft' | 'active' | 'inactive' | 'suspended'
  })

  const [statusForm, setStatusForm] = useState({
    status: 'draft' as 'draft' | 'active' | 'inactive' | 'suspended',
    reason: ''
  })

  // 加载课程列表
  const loadCourses = async () => {
    try {
      setLoading(true)
      const response = await coursesApi.getCourses({
        page: currentPage,
        limit,
        status: statusFilter || undefined,
        search: searchTerm || undefined,
        category: categoryFilter || undefined,
        level: levelFilter || undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      })

      if (response.success) {
        setCourses(response.data)
        setTotal(response.pagination?.total || 0)
        setTotalPages(Math.ceil((response.pagination?.total || 0) / limit))
      }
    } catch (error) {
      console.error('加载课程列表失败:', error)
      toast.error('加载课程列表失败')
    } finally {
      setLoading(false)
    }
  }

  // 加载统计数据
  const loadStats = async () => {
    try {
      const response = await coursesApi.getCourseStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('加载统计数据失败:', error)
    }
  }

  // 加载分类列表
  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getCategories()
      if (response.success) {
        setCategories(response.data)
      }
    } catch (error) {
      console.error('加载分类失败:', error)
    }
  }

  // 获取课程详情
  const loadCourseDetail = async (id: string) => {
    try {
      setLoading(true)
      const response = await coursesApi.getCourseDetail(id)
      if (response.success) {
        setSelectedCourse(response.data)
        setShowDetail(true)
      }
    } catch (error) {
      console.error('获取课程详情失败:', error)
      toast.error('获取课程详情失败')
    } finally {
      setLoading(false)
    }
  }

  // 创建课程
  const handleCreateCourse = async () => {
    if (!courseForm.title || !courseForm.description || !courseForm.language || courseForm.teacher_id === 0) {
      toast.error('请填写必要信息')
      return
    }

    try {
      setLoading(true)
      const response = await coursesApi.createCourse(courseForm)
      
      if (response.success) {
        toast.success('课程创建成功')
        setShowCreateModal(false)
        resetCourseForm()
        loadCourses()
        loadStats()
      }
    } catch (error) {
      console.error('创建课程失败:', error)
      toast.error('创建课程失败')
    } finally {
      setLoading(false)
    }
  }

  // 更新课程
  const handleUpdateCourse = async () => {
    if (!selectedCourse || !courseForm.title || !courseForm.description || !courseForm.language || courseForm.teacher_id === 0) {
      toast.error('请填写必要信息')
      return
    }

    try {
      setLoading(true)
      const response = await coursesApi.updateCourse(selectedCourse.id, courseForm)
      
      if (response.success) {
        toast.success('课程更新成功')
        setShowEditModal(false)
        setSelectedCourse(null)
        resetCourseForm()
        loadCourses()
      }
    } catch (error) {
      console.error('更新课程失败:', error)
      toast.error('更新课程失败')
    } finally {
      setLoading(false)
    }
  }

  // 更新课程状态
  const handleUpdateStatus = async () => {
    if (!selectedCourse) return

    try {
      setLoading(true)
      const response = await coursesApi.updateCourseStatus(selectedCourse.id, statusForm)
      
      if (response.success) {
        toast.success('课程状态更新成功')
        setShowStatusModal(false)
        setSelectedCourse(null)
        loadCourses()
        loadStats()
      }
    } catch (error) {
      console.error('更新课程状态失败:', error)
      toast.error('更新课程状态失败')
    } finally {
      setLoading(false)
    }
  }

  // 删除课程
  const handleDeleteCourse = async () => {
    if (!selectedCourse) return

    try {
      setLoading(true)
      const response = await coursesApi.deleteCourse(selectedCourse.id)
      
      if (response.success) {
        toast.success('课程删除成功')
        setShowDeleteConfirm(false)
        setSelectedCourse(null)
        loadCourses()
        loadStats()
      }
    } catch (error) {
      console.error('删除课程失败:', error)
      toast.error('删除课程失败')
    } finally {
      setLoading(false)
    }
  }

  // 重置表单
  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      duration_minutes: 60,
      max_students: 10,
      price_per_session: 100,
      tags: [],
      language: '',
      level: 'beginner',
      category: '',
      thumbnail_url: '',
      requirements: [],
      objectives: [],
      teacher_id: 0,
      status: 'draft'
    })
  }

  // 编辑课程时初始化表单
  const initEditForm = (course: Course) => {
    setCourseForm({
      title: course.title,
      description: course.description,
      duration_minutes: course.duration_minutes,
      max_students: course.max_students,
      price_per_session: course.price_per_session,
      tags: course.tags || [],
      language: course.language,
      level: course.level,
      category: course.category || '',
      thumbnail_url: course.thumbnail_url || '',
      requirements: course.requirements || [],
      objectives: course.objectives || [],
      teacher_id: course.teacher_id,
      status: course.status
    })
  }

  // 获取状态标签样式
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800'
      case 'suspended':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return '已发布'
      case 'draft':
        return '草稿'
      case 'inactive':
        return '未激活'
      case 'suspended':
        return '已暂停'
      default:
        return '未知'
    }
  }

  // 获取级别文本
  const getLevelText = (level: string) => {
    switch (level) {
      case 'beginner':
        return '初级'
      case 'intermediate':
        return '中级'
      case 'advanced':
        return '高级'
      default:
        return '未知'
    }
  }

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // 添加标签
  const addTag = (tag: string) => {
    if (tag.trim() && !courseForm.tags.includes(tag.trim())) {
      setCourseForm({
        ...courseForm,
        tags: [...courseForm.tags, tag.trim()]
      })
    }
  }

  // 移除标签
  const removeTag = (index: number) => {
    setCourseForm({
      ...courseForm,
      tags: courseForm.tags.filter((_, i) => i !== index)
    })
  }

  // 添加要求
  const addRequirement = (requirement: string) => {
    if (requirement.trim() && !courseForm.requirements.includes(requirement.trim())) {
      setCourseForm({
        ...courseForm,
        requirements: [...courseForm.requirements, requirement.trim()]
      })
    }
  }

  // 移除要求
  const removeRequirement = (index: number) => {
    setCourseForm({
      ...courseForm,
      requirements: courseForm.requirements.filter((_, i) => i !== index)
    })
  }

  // 添加目标
  const addObjective = (objective: string) => {
    if (objective.trim() && !courseForm.objectives.includes(objective.trim())) {
      setCourseForm({
        ...courseForm,
        objectives: [...courseForm.objectives, objective.trim()]
      })
    }
  }

  // 移除目标
  const removeObjective = (index: number) => {
    setCourseForm({
      ...courseForm,
      objectives: courseForm.objectives.filter((_, i) => i !== index)
    })
  }

  // 副作用
  useEffect(() => {
    loadCourses()
    loadStats()
    loadCategories()
  }, [currentPage, statusFilter, categoryFilter, levelFilter, searchTerm, sortBy, sortOrder])

  return (
    <div className="max-w-7xl mx-auto">
      {/* 页面标题 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">课程管理</h1>
            <p className="mt-2 text-gray-600">创建和管理平台课程</p>
          </div>
          <button
            onClick={() => {
              resetCourseForm()
              setShowCreateModal(true)
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            创建课程
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BookOpenIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">总课程数</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">已发布</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">草稿</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.draft}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">平均价格</p>
                <p className="text-2xl font-semibold text-gray-900">¥{stats.price_stats.avg}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 搜索和筛选栏 */}
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* 搜索 */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="搜索课程标题或描述..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* 状态筛选 */}
          <div className="lg:w-32">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">所有状态</option>
              <option value="active">已发布</option>
              <option value="draft">草稿</option>
              <option value="inactive">未激活</option>
              <option value="suspended">已暂停</option>
            </select>
          </div>

          {/* 分类筛选 */}
          <div className="lg:w-32">
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">所有分类</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* 级别筛选 */}
          <div className="lg:w-32">
            <select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">所有级别</option>
              <option value="beginner">初级</option>
              <option value="intermediate">中级</option>
              <option value="advanced">高级</option>
            </select>
          </div>

          {/* 排序 */}
          <div className="lg:w-48">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-')
                setSortBy(sort)
                setSortOrder(order as 'asc' | 'desc')
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="created_at-desc">创建时间（最新）</option>
              <option value="created_at-asc">创建时间（最早）</option>
              <option value="title-asc">标题（A-Z）</option>
              <option value="title-desc">标题（Z-A）</option>
              <option value="price_per_session-asc">价格（低到高）</option>
              <option value="price_per_session-desc">价格（高到低）</option>
            </select>
          </div>
        </div>
      </div>

      {/* 课程列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            课程列表 ({total})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {courses.length === 0 ? (
              <div className="p-8 text-center">
                <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">暂无课程</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter || categoryFilter || levelFilter ? '尝试调整搜索条件' : '还没有创建课程'}
                </p>
              </div>
            ) : (
              courses.map((course) => (
                <div key={course.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        {course.thumbnail_url ? (
                          <img
                            src={course.thumbnail_url}
                            alt={course.title}
                            className="w-16 h-16 rounded-lg object-cover mr-4"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                            <BookOpenIcon className="h-8 w-8 text-purple-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h4 className="text-lg font-medium text-gray-900 mr-3">
                              {course.title}
                            </h4>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(course.status)}`}>
                              {getStatusText(course.status)}
                            </span>
                          </div>
                          <div className="flex items-center mt-1 text-sm text-gray-500">
                            <span className="mr-4">教师: {course.teacher_name}</span>
                            <span className="mr-4">语言: {course.language}</span>
                            <span className="mr-4">级别: {getLevelText(course.level)}</span>
                            {course.category && (
                              <span className="mr-4">分类: {course.category}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 课程描述 */}
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {course.description}
                        </p>
                      </div>

                      {/* 课程信息 */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">时长</p>
                          <p className="text-sm text-gray-600">{course.duration_minutes}分钟</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">价格</p>
                          <p className="text-sm text-gray-600">¥{course.price_per_session}/课时</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">学生上限</p>
                          <p className="text-sm text-gray-600">{course.max_students}人</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">评分</p>
                          <div className="flex items-center">
                            <StarIcon className="h-4 w-4 text-yellow-500 mr-1" />
                            <span className="text-sm text-gray-600">{course.average_rating}</span>
                            <span className="text-sm text-gray-500 ml-1">({course.review_count})</span>
                          </div>
                        </div>
                      </div>

                      {/* 统计信息 */}
                      <div className="flex items-center space-x-6 text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          <span>完成课时: {course.completed_sessions}</span>
                        </div>
                        <div className="flex items-center">
                          <ChartBarIcon className="h-4 w-4 mr-1" />
                          <span>完成率: {course.completion_rate}%</span>
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <span>创建时间: {formatDate(course.created_at)}</span>
                        </div>
                      </div>

                      {/* 标签 */}
                      {course.tags && course.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {course.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                          {course.tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{course.tags.length - 3}个标签</span>
                          )}
                        </div>
                      )}

                      {/* 操作按钮 */}
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          最后更新: {formatDate(course.updated_at)}
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => loadCourseDetail(course.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            查看详情
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCourse(course)
                              initEditForm(course)
                              setShowEditModal(true)
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            编辑
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCourse(course)
                              setStatusForm({
                                status: course.status,
                                reason: ''
                              })
                              setShowStatusModal(true)
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <ClockIcon className="h-4 w-4 mr-1" />
                            状态
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCourse(course)
                              setShowDeleteConfirm(true)
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            删除
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                显示第 {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} 条，共 {total} 条
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  上一页
                </button>
                <span className="text-sm text-gray-700">
                  第 {currentPage} / {totalPages} 页
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 创建课程弹窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowCreateModal(false)}
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      创建新课程
                    </h3>

                    <div className="space-y-6">
                      {/* 基本信息 */}
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">基本信息</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程标题 *
                            </label>
                            <input
                              type="text"
                              value={courseForm.title}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                title: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="请输入课程标题"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程描述 *
                            </label>
                            <textarea
                              value={courseForm.description}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                description: e.target.value
                              })}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="请输入课程描述"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程语言 *
                            </label>
                            <input
                              type="text"
                              value={courseForm.language}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                language: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="如：中文、英文"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程级别
                            </label>
                            <select
                              value={courseForm.level}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                level: e.target.value as 'beginner' | 'intermediate' | 'advanced'
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="beginner">初级</option>
                              <option value="intermediate">中级</option>
                              <option value="advanced">高级</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程分类
                            </label>
                            <input
                              type="text"
                              value={courseForm.category}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                category: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="如：商务英语、日常口语"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              教师ID *
                            </label>
                            <input
                              type="number"
                              value={courseForm.teacher_id}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                teacher_id: parseInt(e.target.value) || 0
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="请输入教师用户ID"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 课程设置 */}
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">课程设置</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程时长（分钟）
                            </label>
                            <input
                              type="number"
                              value={courseForm.duration_minutes}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                duration_minutes: parseInt(e.target.value) || 60
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="15"
                              max="240"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              学生上限
                            </label>
                            <input
                              type="number"
                              value={courseForm.max_students}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                max_students: parseInt(e.target.value) || 10
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="1"
                              max="100"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              价格（元/课时）
                            </label>
                            <input
                              type="number"
                              value={courseForm.price_per_session}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                price_per_session: parseFloat(e.target.value) || 0
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 标签 */}
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">课程标签</h4>
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {courseForm.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full"
                              >
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => removeTag(index)}
                                  className="ml-1 text-purple-600 hover:text-purple-800"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex">
                            <input
                              type="text"
                              placeholder="添加标签后按回车"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  addTag((e.target as HTMLInputElement).value)
                                  ;(e.target as HTMLInputElement).value = ''
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const input = document.querySelector('input[placeholder="添加标签后按回车"]') as HTMLInputElement
                                if (input) {
                                  addTag(input.value)
                                  input.value = ''
                                }
                              }}
                              className="px-3 py-2 bg-purple-600 text-white rounded-r-md hover:bg-purple-700"
                            >
                              添加
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* 状态 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          课程状态
                        </label>
                        <select
                          value={courseForm.status}
                          onChange={(e) => setCourseForm({
                            ...courseForm,
                            status: e.target.value as 'draft' | 'active' | 'inactive' | 'suspended'
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="draft">草稿</option>
                          <option value="active">已发布</option>
                          <option value="inactive">未激活</option>
                          <option value="suspended">已暂停</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCreateCourse}
                  disabled={loading}
                >
                  {loading ? '创建中...' : '创建课程'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowCreateModal(false)}
                  disabled={loading}
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 编辑课程弹窗 */}
      {showEditModal && selectedCourse && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowEditModal(false)}
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      编辑课程: {selectedCourse.title}
                    </h3>

                    <div className="space-y-6">
                      {/* 基本信息 */}
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">基本信息</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程标题 *
                            </label>
                            <input
                              type="text"
                              value={courseForm.title}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                title: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程描述 *
                            </label>
                            <textarea
                              value={courseForm.description}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                description: e.target.value
                              })}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程语言 *
                            </label>
                            <input
                              type="text"
                              value={courseForm.language}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                language: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程级别
                            </label>
                            <select
                              value={courseForm.level}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                level: e.target.value as 'beginner' | 'intermediate' | 'advanced'
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="beginner">初级</option>
                              <option value="intermediate">中级</option>
                              <option value="advanced">高级</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程分类
                            </label>
                            <input
                              type="text"
                              value={courseForm.category}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                category: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              教师ID *
                            </label>
                            <input
                              type="number"
                              value={courseForm.teacher_id}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                teacher_id: parseInt(e.target.value) || 0
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 课程设置 */}
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">课程设置</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              课程时长（分钟）
                            </label>
                            <input
                              type="number"
                              value={courseForm.duration_minutes}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                duration_minutes: parseInt(e.target.value) || 60
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="15"
                              max="240"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              学生上限
                            </label>
                            <input
                              type="number"
                              value={courseForm.max_students}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                max_students: parseInt(e.target.value) || 10
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="1"
                              max="100"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              价格（元/课时）
                            </label>
                            <input
                              type="number"
                              value={courseForm.price_per_session}
                              onChange={(e) => setCourseForm({
                                ...courseForm,
                                price_per_session: parseFloat(e.target.value) || 0
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 状态 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          课程状态
                        </label>
                        <select
                          value={courseForm.status}
                          onChange={(e) => setCourseForm({
                            ...courseForm,
                            status: e.target.value as 'draft' | 'active' | 'inactive' | 'suspended'
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="draft">草稿</option>
                          <option value="active">已发布</option>
                          <option value="inactive">未激活</option>
                          <option value="suspended">已暂停</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleUpdateCourse}
                  disabled={loading}
                >
                  {loading ? '更新中...' : '更新课程'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowEditModal(false)}
                  disabled={loading}
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 更新状态弹窗 */}
      {showStatusModal && selectedCourse && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowStatusModal(false)}
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      更新课程状态
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      课程: <span className="font-medium">{selectedCourse.title}</span>
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          新状态
                        </label>
                        <select
                          value={statusForm.status}
                          onChange={(e) => setStatusForm({
                            ...statusForm,
                            status: e.target.value as 'draft' | 'active' | 'inactive' | 'suspended'
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="draft">草稿</option>
                          <option value="active">已发布</option>
                          <option value="inactive">未激活</option>
                          <option value="suspended">已暂停</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          变更原因（可选）
                        </label>
                        <textarea
                          value={statusForm.reason}
                          onChange={(e) => setStatusForm({
                            ...statusForm,
                            reason: e.target.value
                          })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="请说明状态变更的原因..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleUpdateStatus}
                  disabled={loading}
                >
                  {loading ? '更新中...' : '确认更新'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowStatusModal(false)}
                  disabled={loading}
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认弹窗 */}
      {showDeleteConfirm && selectedCourse && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowDeleteConfirm(false)}
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      删除课程
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        确定要删除课程 <span className="font-medium">{selectedCourse.title}</span> 吗？此操作不可撤销。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteCourse}
                  disabled={loading}
                >
                  {loading ? '删除中...' : '确认删除'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
